import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adMetadataApi } from '../lib/api';
import { ChevronDownIcon, ChevronUpIcon, MapPinIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface AdMetadataFormProps {
  uploadId: string;
  readOnly?: boolean;
  defaultExpanded?: boolean;
}

const MEDIUM_OPTIONS = [
  { value: 'NEWSPAPER', label: 'Newspaper' },
  { value: 'MAGAZINE', label: 'Magazine' },
  { value: 'RADIO', label: 'Radio' },
  { value: 'TV', label: 'TV' },
  { value: 'DIRECT_MAIL', label: 'Direct Mail' },
  { value: 'BILLBOARD', label: 'Billboard' },
  { value: 'SOCIAL_MEDIA', label: 'Social Media' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'OTHER', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BI_WEEKLY', label: 'Bi-Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ONGOING', label: 'Ongoing' },
];

const AD_TYPE_OPTIONS = [
  { value: 'GENERIC', label: 'Generic' },
  { value: 'DEPOSIT_RELATED', label: 'Deposit Related' },
  { value: 'CREDIT_RELATED', label: 'Credit Related' },
  { value: 'COMBINED', label: 'Combined' },
];

export default function AdMetadataForm({ uploadId, readOnly = false, defaultExpanded = false }: AdMetadataFormProps) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [form, setForm] = useState<Record<string, any>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data: metadataData, isLoading } = useQuery({
    queryKey: ['adMetadata', uploadId],
    queryFn: async () => {
      const response = await adMetadataApi.getMetadata(uploadId);
      return response.data;
    },
    enabled: !!uploadId,
  });

  useEffect(() => {
    if (metadataData?.metadata) {
      const m = metadataData.metadata;
      setForm({
        medium: m.medium || '',
        mediumOther: m.mediumOther || '',
        distribution: m.distribution || '',
        includesMMCTs: m.includesMMCTs ?? null,
        assessmentArea: m.assessmentArea || '',
        adDate: m.adDate ? new Date(m.adDate).toISOString().split('T')[0] : '',
        frequency: m.frequency || '',
        adType: m.adType || '',
        productsAdvertised: m.productsAdvertised || '',
        hasHumanImages: m.hasHumanImages ?? null,
        gender: m.gender || '',
        race1: m.race1 || '',
        race2: m.race2 || '',
        race3: m.race3 || '',
        ethnicity: m.ethnicity || '',
        comments: m.comments || '',
      });
      setHasChanges(false);
    }
  }, [metadataData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return adMetadataApi.updateMetadata(uploadId, {
        ...form,
        medium: form.medium || null,
        frequency: form.frequency || null,
        adType: form.adType || null,
        adDate: form.adDate || null,
      });
    },
    onSuccess: () => {
      toast.success('Ad placement details saved');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['adMetadata', uploadId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save ad details');
    },
  });

  const updateField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const selectClass = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500';
  const inputClass = selectClass;
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MapPinIcon className="h-5 w-5 text-ocean-600" />
          <span className="text-sm font-semibold text-navy-900">Ad Placement Details</span>
          {metadataData?.metadata && (
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">Saved</span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : (
            <>
              {/* Row 1: Medium, Distribution, MMCTs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Medium</label>
                  <select
                    value={form.medium || ''}
                    onChange={(e) => updateField('medium', e.target.value)}
                    disabled={readOnly}
                    className={selectClass}
                  >
                    <option value="">Select medium...</option>
                    {MEDIUM_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Distribution</label>
                  <input
                    type="text"
                    value={form.distribution || ''}
                    onChange={(e) => updateField('distribution', e.target.value)}
                    disabled={readOnly}
                    placeholder="e.g., Metro area, Statewide"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Includes MMCTs?</label>
                  <select
                    value={form.includesMMCTs === true ? 'yes' : form.includesMMCTs === false ? 'no' : ''}
                    onChange={(e) => updateField('includesMMCTs', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                    disabled={readOnly}
                    className={selectClass}
                  >
                    <option value="">Not specified</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              {/* Medium Other (shown when OTHER selected) */}
              {form.medium === 'OTHER' && (
                <div>
                  <label className={labelClass}>Other Medium Description</label>
                  <input
                    type="text"
                    value={form.mediumOther || ''}
                    onChange={(e) => updateField('mediumOther', e.target.value)}
                    disabled={readOnly}
                    placeholder="Describe the medium"
                    className={inputClass}
                  />
                </div>
              )}

              {/* Row 2: Assessment Area, Date, Frequency */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Assessment Area</label>
                  <input
                    type="text"
                    value={form.assessmentArea || ''}
                    onChange={(e) => updateField('assessmentArea', e.target.value)}
                    disabled={readOnly}
                    placeholder="CRA assessment area"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Ad Date</label>
                  <input
                    type="date"
                    value={form.adDate || ''}
                    onChange={(e) => updateField('adDate', e.target.value)}
                    disabled={readOnly}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Frequency</label>
                  <select
                    value={form.frequency || ''}
                    onChange={(e) => updateField('frequency', e.target.value)}
                    disabled={readOnly}
                    className={selectClass}
                  >
                    <option value="">Select frequency...</option>
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Ad Type, Products Advertised */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Type of Ad</label>
                  <select
                    value={form.adType || ''}
                    onChange={(e) => updateField('adType', e.target.value)}
                    disabled={readOnly}
                    className={selectClass}
                  >
                    <option value="">Select type...</option>
                    {AD_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Products Advertised</label>
                  <input
                    type="text"
                    value={form.productsAdvertised || ''}
                    onChange={(e) => updateField('productsAdvertised', e.target.value)}
                    disabled={readOnly}
                    placeholder="e.g., Savings Account, Auto Loan"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Demographics section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">Demographics (Fair Lending / CRA)</h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Human Images?</label>
                    <select
                      value={form.hasHumanImages === true ? 'yes' : form.hasHumanImages === false ? 'no' : ''}
                      onChange={(e) => updateField('hasHumanImages', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                      disabled={readOnly}
                      className={selectClass}
                    >
                      <option value="">Not specified</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Gender</label>
                    <input
                      type="text"
                      value={form.gender || ''}
                      onChange={(e) => updateField('gender', e.target.value)}
                      disabled={readOnly}
                      placeholder="e.g., Male, Female, Both"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Ethnicity</label>
                    <input
                      type="text"
                      value={form.ethnicity || ''}
                      onChange={(e) => updateField('ethnicity', e.target.value)}
                      disabled={readOnly}
                      placeholder="e.g., Hispanic, Non-Hispanic"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <div>
                    <label className={labelClass}>Race 1</label>
                    <input
                      type="text"
                      value={form.race1 || ''}
                      onChange={(e) => updateField('race1', e.target.value)}
                      disabled={readOnly}
                      placeholder="Primary race representation"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Race 2</label>
                    <input
                      type="text"
                      value={form.race2 || ''}
                      onChange={(e) => updateField('race2', e.target.value)}
                      disabled={readOnly}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Race 3</label>
                    <input
                      type="text"
                      value={form.race3 || ''}
                      onChange={(e) => updateField('race3', e.target.value)}
                      disabled={readOnly}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className={labelClass}>Comments</label>
                <textarea
                  value={form.comments || ''}
                  onChange={(e) => updateField('comments', e.target.value)}
                  disabled={readOnly}
                  rows={2}
                  placeholder="Additional notes about this ad placement..."
                  className={inputClass}
                />
              </div>

              {/* Save button */}
              {!readOnly && (
                <div className="flex justify-end">
                  <button
                    onClick={() => saveMutation.mutate()}
                    disabled={!hasChanges || saveMutation.isPending}
                    className="px-4 py-2 bg-ocean-600 text-white text-sm font-medium rounded-md hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saveMutation.isPending ? 'Saving...' : 'Save Ad Details'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
